import React from 'react';
import {
    Viewer,
    Cartographic,
    Cartesian3,
    CesiumTerrainProvider,
    Cesium3DTileset,
    createWorldTerrainAsync,
    Math as CesiumMath,
    Matrix3,
    Matrix4,
    HeadingPitchRoll,
    HeightReference,
    Model,
    Ion,
    IonImageryProvider,
    IonResource,
    JulianDate,
    LabelStyle,
    SceneMode,
    Color,
    DirectionalLight,
    sampleTerrainMostDetailed,
    Transforms,
    VerticalOrigin,
    ColorBlendMode
} from 'cesium';
import {
    summaryContent
} from './summaries';

export function centerMap(mapRef, coordinates, zoom) {
    mapRef.current.target.setView(coordinates, zoom);
}
export function footnote(number) {
    return (<sup>{number}</sup>);
}

export function nap(ms) {
    return Promise((resolve) => setTimeout(resolve, ms));
}

// Map-specific utilities

export function generateLandmarkLabel(feature, currentYear) {
    let landmarkLabel = '';

    if (currentYear === 1957) {
        if (feature.properties.estate_name_1957 !== null) {
            landmarkLabel = feature.properties.estate_name_1957;
        } else if (feature.properties.estate_name !== null) {
            landmarkLabel = feature.properties.estate_name;
        } else {
            landmarkLabel = feature.properties.name;
        }
    } else if (currentYear === 1915) {
        if (feature.properties.estate_name !== null) {
            landmarkLabel = feature.properties.estate_name;
        }
    } else if (currentYear === 1870) {
        if (feature.properties.estate_name !== null) {
            landmarkLabel = feature.properties.estate_name;
        } else if (feature.properties.owner_1870ish !== null) {
            landmarkLabel = feature.properties.owner_1870ish;
        } else if (feature.properties.owner_1860 !== null) {
            landmarkLabel = feature.properties.owner_1860;
        } else {
            landmarkLabel = feature.properties.name;
        }
    } else if (currentYear === 1830) {
        if (feature.properties.estate_name !== null) {
            landmarkLabel = feature.properties.estate_name;
        } else if (feature.properties.owner_1820 !== null) {
            landmarkLabel = feature.properties.owner_1820;
        } else {
            landmarkLabel = feature.properties.name;
        }
    } else if (feature.properties.estate_name_1957 !== null) {
        landmarkLabel = feature.properties.estate_name_1957;
    } else if (feature.properties.name !== null) {
        landmarkLabel = feature.properties.name;
    }
    return landmarkLabel;
}

export function generatePlaceLabel(feature, currentYear) {
    let field = 'name_1960';
    if (currentYear === 1830) {
        field = 'name_1800';
    }
    if (currentYear === 1870) {
        field = 'name_1860';
    }
    if (currentYear === 1915) {
        field = null;
    }
    if (currentYear === 1994 || currentYear === 'today') {
        field = 'name_today';
    }
    return feature.properties[field];
}
export function generateLandGrantColor(acreage, max) {
    return `hsl(${360 * ((acreage / max))},${25 + 70 * (acreage / max)}%,${
        60 + 10 * (acreage / max)
    }%)`;
}

export function getFileNameWithoutExtension(filePath) {
    // Use the lastIndexOf method to find the last occurrence of "/"
    // or "\" in the filePath to get the filename without the path
    const pathSeparatorIndex = Math.max(filePath.lastIndexOf('/'), filePath.lastIndexOf('\\'));
    let fileNameWithoutPath = filePath.substring(pathSeparatorIndex + 1);

    // Use the indexOf method to find the first occurrence of "." in the filename
    const dotIndex = fileNameWithoutPath.lastIndexOf('.');

    // If a dot is found and it's not the first character (e.g., for hidden files),
    // remove the file extension
    if (dotIndex !== -1 && dotIndex !== 0) {
        fileNameWithoutPath = fileNameWithoutPath.substring(0, dotIndex);
    }

    return fileNameWithoutPath;
}

export function coordinateCopyCallback(e) {
    // Get the coordinates from the right-click event
    const { lat, lng } = e.latlng;
    // Create a temporary input element to copy the coordinates to the clipboard
    const input = document.createElement('input');
    input.value = `${lat}, ${lng}`;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    // You can also display a notification to indicate that the coordinates have been copied.
    console.log(`Coordinates copied: ${lat}, ${lng}`);
}
export function generateStreetViewLink(photo) {
    return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=
    ${photo.coordinates[0]}%2c${photo.coordinates[1]}&heading=${photo.heading}`;
}

export function generateMapsLink(photo) {
    return `https://www.google.com/maps/search/?api=1&query=${photo.coordinates[0]},${photo.coordinates[1]}`;
}

export function openToGoogleCallback(e) {
    // Get the coordinates from the right-click event
    const { lat, lng } = e.latlng;
    window.open(generateMapsLink({ coordinates: [lat, lng] }), '_blank');
}

export function shareLandmarkLink(uid) {
    const currentHost = window.location.host;
    const input = document.createElement('input');
    input.value = `${currentHost}/landmark?uid=${uid}`;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
}

export function shareSummaryLink(id) {
    const currentHost = window.location.host;
    const input = document.createElement('input');
    input.value = `${currentHost}/summary?id=${id}`;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
}

export function sharePhotoLink(id) {
    const currentHost = window.location.host;
    const input = document.createElement('input');
    input.value = `${currentHost}/image?id=${id}`;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
}

export function getFunctionalYear(currentYear) {
    let functionalYear = 1957;
    if (currentYear === 1957) {
        functionalYear = 1960;
    } else if (currentYear === 1870) {
        functionalYear = 1860;
    } else if (currentYear === 1830) {
        functionalYear = 1800;
    }
    return functionalYear;
}

export function isSummaryPopulated(communityId, place, currentYear, placeName) {
    const functionalYear = getFunctionalYear(currentYear);
    let foundId = null;
    if (place) {
        foundId = Object.keys(summaryContent).find((key) => summaryContent[key].name === place.properties[`name_${functionalYear}`] || summaryContent[key].altName === place.properties[`name_${functionalYear}`]);
    } else if (communityId) {
        foundId = communityId;
    } else if (placeName) {
        foundId = Object.keys(summaryContent).find((key) => summaryContent[key].name === placeName);
    }
    if (foundId && summaryContent[foundId].summary.length > 0) {
        return foundId;
    }
    return false;
}

export function isSameView(v1, v2) {
    if (!v1 || !v2) return false;

    return (
        Math.abs(v1.lat - v2.lat) < 0.000001
        && Math.abs(v1.lon - v2.lon) < 0.000001
        && Math.abs(v1.height - v2.height) < 0.05
        && Math.abs(v1.heading - v2.heading) < 0.0001
        && Math.abs(v1.pitch - v2.pitch) < 0.0001
        && Math.abs(v1.roll - v2.roll) < 0.0001
    );
}

export async function addSmoothTerrainLine(viewer, terrainProvider, inputPoints, samplesPerSegment = 30) {
    if (inputPoints.length < 2) {
        console.error('Need at least two points to create a line.');
        return;
    }

    const cartoPoints = [];

    for (let i = 0; i < inputPoints.length - 1; i += 1) {
        const start = Cartographic.fromDegrees(inputPoints[i][1], inputPoints[i][0]);
        const end = Cartographic.fromDegrees(inputPoints[i + 1][1], inputPoints[i + 1][0]);

        for (let j = 0; j <= samplesPerSegment; j += 1) {
            const t = j / samplesPerSegment;

            const lon = CesiumMath.lerp(start.longitude, end.longitude, t);
            const lat = CesiumMath.lerp(start.latitude, end.latitude, t);

            cartoPoints.push(new Cartographic(lon, lat));
        }
    }

    const updated = await sampleTerrainMostDetailed(terrainProvider, cartoPoints);

    const positions = updated.map((cart) => Cartesian3.fromRadians(
        cart.longitude,
        cart.latitude,
        cart.height + 1.0 // 1 meter offset
    ));

    viewer.entities.add({
        name: 'Multi-segment terrain-following line',
        polyline: {
            positions,
            width: 1,
            material: Color.fromCssColorString('#5B5445'),
            clampToGround: false
        }
    });
}

export async function loadTreeAtGround(viewer, terrainProvider, lat, lon, scale, heading, modelPath) {
    // Sample terrain height at the lat/lon
    const carto = Cartographic.fromDegrees(lon, lat);
    const [updated] = await sampleTerrainMostDetailed(terrainProvider, [carto]);
    const height = updated.height || 0;

    // Compute world position from lat/lon/height
    const position = Cartesian3.fromRadians(carto.longitude, carto.latitude, height);

    // Random heading (0 to 360 degrees)
    const pitch = 0;
    const roll = 0;

    // Build the HPR rotation matrix
    const hpr = new HeadingPitchRoll(heading, pitch, roll);
    const orientation = Matrix3.fromHeadingPitchRoll(hpr);

    // Combine position + orientation
    const fixedFrame = Transforms.eastNorthUpToFixedFrame(position);
    const modelMatrix = Matrix4.multiplyByMatrix3(fixedFrame, orientation, new Matrix4());

    // Load and add the model
    const model = await Model.fromGltfAsync({
        url: modelPath,
        modelMatrix,
        scale
    });

    viewer.scene.primitives.add(model);
}

export async function addModelClamped(viewer, terrainProvider, modelPath, lat, lon, scale, headingDegrees, offset) {
    // Define geographic location of the model
    // 1. Convert to Cartographic
    const carto = Cartographic.fromDegrees(lon, lat);

    // 2. Sample terrain height at this point
    const [updated] = await sampleTerrainMostDetailed(terrainProvider, [carto]);

    const position = Cartesian3.fromRadians(
        updated.longitude,
        updated.latitude,
        offset ? updated.height + offset : updated.height
    );

    // Convert heading to radians and build rotation matrix
    const heading = CesiumMath.toRadians(headingDegrees);
    const pitch = 0.0;
    const roll = 0.0;

    const hpr = new HeadingPitchRoll(heading, pitch, roll);
    const orientation = Matrix3.fromHeadingPitchRoll(hpr);
    const fixedFrame = Transforms.eastNorthUpToFixedFrame(position);
    const modelMatrix = Matrix4.multiplyByMatrix3(fixedFrame, orientation, new Matrix4());

    // 4. Load and position the model
    const model = await Model.fromGltfAsync({
        url: modelPath, // path to your GLB file
        modelMatrix,
        scale
    });

    // 5. Add to the scene
    viewer.scene.primitives.add(model);
}

export async function addModel(viewer, terrainProvider, modelPath, lat, lon, scale, headingDegrees, height) {
    // Define geographic location of the model
    // 1. Convert to Cartographic
    const carto = Cartographic.fromDegrees(lon, lat);

    // 2. Sample terrain height at this point
    const [updated] = await sampleTerrainMostDetailed(terrainProvider, [carto]);

    const position = Cartesian3.fromRadians(
        updated.longitude,
        updated.latitude,
        height
    );

    // Convert heading to radians and build rotation matrix
    const heading = CesiumMath.toRadians(headingDegrees);
    const pitch = 0.0;
    const roll = 0.0;

    const hpr = new HeadingPitchRoll(heading, pitch, roll);
    const orientation = Matrix3.fromHeadingPitchRoll(hpr);
    const fixedFrame = Transforms.eastNorthUpToFixedFrame(position);
    const modelMatrix = Matrix4.multiplyByMatrix3(fixedFrame, orientation, new Matrix4());

    // 4. Load and position the model
    const model = await Model.fromGltfAsync({
        url: modelPath, // path to your GLB file
        modelMatrix,
        scale,
        color: Color.WHITE.withAlpha(1.0),
        colorBlendMode: ColorBlendMode.MIX,
        colorBlendAmount: 0.25
    });

    // 5. Add to the scene
    viewer.scene.primitives.add(model);
}

export function addLabelAboveBuilding(viewer, lat, lon, height, text) {
    viewer.entities.add({
        position: Cartesian3.fromDegrees(lon, lat, height + 10),
        label: {
            text,
            font: '16px "Times New Roman", serif',
            fillColor: Color.WHITE,
            outlineColor: Color.BLACK,
            outlineWidth: 2,
            style: LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: VerticalOrigin.BOTTOM,
            heightReference: HeightReference.NONE,
            showBackground: true,
            backgroundColor: new Color(0, 0, 0, 0.4),
            pixelOffset: new Cartesian3(0, -10, 0),
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
